import { useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Stack,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useAppTheme } from '@/theme/ThemeContext'
import TitleIcons from '@assets/Frame 11250.svg'
import { Step1Upload } from './UpdateConsumerSteps/Step1Upload'
import { Step2ColumnMapping } from './UpdateConsumerSteps/Step2ColumnMapping'
import { Step3KeyColumnSelection } from './UpdateConsumerSteps/Step3KeyColumnSelection'
import { Step4DataVerification } from './UpdateConsumerSteps/Step4DataVerification'
import { CircularProgress } from '@mui/material'

type Step = {
  id: number
  label: string
}

const steps: Step[] = [
  { id: 1, label: 'העלאת קובץ' },
  { id: 2, label: 'מיפוי עמודות' },
  { id: 3, label: 'בחירת עמודת מפתח' },
  { id: 4, label: 'אימות נתונים' },
]

interface UpdateConsumersModalProps {
  open: boolean
  onClose: () => void
}

export default function UpdateConsumersModal({
  open,
  onClose,
}: UpdateConsumersModalProps) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [activeStep, setActiveStep] = useState(1)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [useFileHeaders, setUseFileHeaders] = useState(false)
  const [mappings, setMappings] = useState<Record<string, string>>({})
  const [removedColumns, setRemovedColumns] = useState<Set<string>>(new Set())
  const [selectedKeyColumns, setSelectedKeyColumns] = useState<string[]>([
    'water_meter',
  ])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleMappingChange = (systemId: string, fileColumn: string) => {
    setMappings(prev => ({ ...prev, [systemId]: fileColumn }))
  }

  const handleRemoveMapping = (systemId: string) => {
    setRemovedColumns(prev => {
      const next = new Set(prev)
      next.add(systemId)
      return next
    })
  }

  const handleRestoreMapping = (systemId: string) => {
    setRemovedColumns(prev => {
      const next = new Set(prev)
      next.delete(systemId)
      return next
    })
  }

  const handleKeyColumnToggle = (systemId: string) => {
    setSelectedKeyColumns(prev =>
      prev.includes(systemId)
        ? prev.filter(id => id !== systemId)
        : [...prev, systemId]
    )
  }

  const TEXT_PRIMARY = isDarkMode ? '#BBD7FF' : '#003F61'
  const TEXT_SECONDARY = isDarkMode ? '#BBD7FF' : '#64748B'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
          backgroundImage: 'none',
          direction: 'rtl',
          p: '40px',
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 5,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={TitleIcons}
              alt="Icons"
              style={{
                width: 45,
                height: 32,
              }}
            />
          </Box>
          <Typography
            sx={{
              color: isDarkMode ? '#BBD7FF' : '#003F61',
              fontWeight: 700,
              fontSize: '20px',
              fontFamily: 'Rubik',
            }}
          >
            הקמת צרכנים ו/או עדכון פרטים
          </Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: '#003F61', p: 0 }}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          mb: 4,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        {/* Step 1: Upload Section */}
        {activeStep === 1 && (
          <Step1Upload
            selectedFile={selectedFile}
            onFileChange={setSelectedFile}
          />
        )}

        {/* Step 2: Column Mapping */}
        {activeStep === 2 && (
          <Step2ColumnMapping
            useFileHeaders={useFileHeaders}
            onUseFileHeadersChange={setUseFileHeaders}
            mappings={mappings}
            removedColumns={removedColumns}
            onMappingChange={handleMappingChange}
            onRemoveMapping={handleRemoveMapping}
            onRestoreMapping={handleRestoreMapping}
          />
        )}

        {/* Step 3: Key Column Selection */}
        {activeStep === 3 && (
          <Step3KeyColumnSelection
            selectedKeyColumns={selectedKeyColumns}
            onKeyColumnToggle={handleKeyColumnToggle}
            mappings={mappings}
          />
        )}

        {/* Step 4: Data Verification */}
        {activeStep === 4 && !isSuccess && (
          <Step4DataVerification
            mappings={mappings}
            selectedKeyColumns={selectedKeyColumns}
            onBack={() => setActiveStep(3)}
            onConfirm={() => {
              setIsProcessing(true)
              setTimeout(() => {
                setIsProcessing(false)
                setIsSuccess(true)
              }, 2000)
            }}
          />
        )}

        {/* Success State */}
        {isSuccess && (
          <Box
            sx={{
              height: '500px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <Box
              sx={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                bgcolor: 'rgba(34, 197, 94, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ color: '#22C55E', fontSize: 48 }} />
            </Box>
            <Typography
              sx={{
                color: TEXT_PRIMARY,
                fontSize: '24px',
                fontWeight: 700,
                fontFamily: 'Rubik',
              }}
            >
              הנתונים עודכנו בהצלחה!
            </Typography>
            <Typography
              sx={{
                color: TEXT_SECONDARY,
                fontSize: '16px',
                fontFamily: 'Rubik',
                textAlign: 'center',
              }}
            >
              כל הנתונים התקינים עובדו והתעדכנו במערכת.
              <br />
              ניתן לראות את השינויים בטבלת הצרכנים.
            </Typography>
          </Box>
        )}

        {/* Other steps placeholders */}
        {activeStep > 4 && !isSuccess && (
          <Box
            sx={{
              height: '500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color={TEXT_PRIMARY}>תוכן שלב {activeStep}</Typography>
          </Box>
        )}
      </DialogContent>

      {!isSuccess && activeStep < 4 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Footer Buttons - On the Left, Above Stepper */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 1,
              mt: -10,
              gap: 2,
            }}
          >
            <Button
              onClick={() => setActiveStep(prev => prev - 1)}
              sx={{
                bgcolor: '#EFF2F7',
                color: '#003F61',
                px: 4,
                py: 0.8,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Rubik',
                visibility:
                  activeStep > 1 && !isProcessing && !isSuccess
                    ? 'visible'
                    : 'hidden',
              }}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              disabled={(activeStep === 1 && !selectedFile) || isProcessing}
              onClick={() => {
                if (activeStep < steps.length) {
                  setActiveStep(prev => prev + 1)
                } else if (isSuccess) {
                  onClose()
                } else {
                  // Final save action
                  setIsProcessing(true)
                  // Simulate API call
                  setTimeout(() => {
                    setIsProcessing(false)
                    setIsSuccess(true)
                  }, 2000)
                }
              }}
              sx={{
                bgcolor: isDarkMode
                  ? '#003089'
                  : activeStep === steps.length || isSuccess
                    ? '#3B82F6'
                    : '#F0F7FF',
                color: isDarkMode
                  ? '#BBD7FF'
                  : activeStep === steps.length || isSuccess
                    ? '#FFFFFF'
                    : '#3B82F6',
                boxShadow: 'none',
                px: 4,
                py: 0.8,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Rubik',
                '&:hover': {
                  bgcolor: isDarkMode
                    ? '#003BA8'
                    : activeStep === steps.length || isSuccess
                      ? '#2563EB'
                      : '#E1EFFF',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: isDarkMode ? '#1e293b' : '#f1f5f9',
                  color: isDarkMode ? '#475569' : '#94a3b8',
                },
              }}
            >
              {isProcessing ? (
                <CircularProgress size={20} sx={{ color: '#FFF' }} />
              ) : isSuccess ? (
                'סיום'
              ) : activeStep === steps.length ? (
                'שמור וסיים'
              ) : (
                'המשך'
              )}
            </Button>
          </Box>
        </Box>
      )}

      {/* Stepper - Always visible at bottom, or handled specifically for success */}
      {!isProcessing && (
        <Box
          sx={{
            width: '100%',
            px: 2,
            mt: isSuccess || activeStep === 4 ? 4 : 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 3,
              justifyContent: 'center',
            }}
          >
            {steps.map((step, index) => {
              const isActive = activeStep === step.id
              const isCompleted = activeStep > step.id || isSuccess
              const stepBg =
                isActive || isCompleted
                  ? '#72A4EB'
                  : isDarkMode
                    ? '#1E293B'
                    : '#E2E8F0'
              const stepTextColor =
                isActive || isCompleted
                  ? isDarkMode
                    ? '#72A4EB'
                    : '#72A4EB'
                  : isDarkMode
                    ? '#BBD7FF'
                    : '#003F61'

              return (
                <Box
                  key={step.id}
                  sx={{
                    flex: '1 1 0',
                    maxWidth: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '8px',
                      bgcolor: stepBg,
                      position: 'relative',
                      transition: 'all 0.3s',
                      clipPath:
                        index === 0
                          ? 'polygon(5% 0%, 100% 0%, 100% 100%, 5% 100%, 0% 50%)'
                          : 'polygon(5% 0%, 100% 0%, 95% 50%, 100% 100%, 5% 100%, 0% 50%)',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: isActive || isCompleted ? 700 : 400,
                      fontFamily: 'Rubik',
                      color: stepTextColor,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
      )}
    </Dialog>
  )
}
